
import React, { useMemo, useState } from "react";
import {
  CreditCard,
  CheckCircle2,
  Clock3,
  RotateCcw,
  Search,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  ReceiptText,
  Smartphone,
  Wallet
} from "lucide-react";

const initialPayments = [
  {
    id: 1,
    transactionId: "TXN202610001",
    memberId: "SKM001",
    memberName: "Arun Kumar",
    plan: "Gold Plan",
    amount: 3999,
    method: "UPI",
    status: "Success",
    paymentDate: "2026-10-13",
    paymentTime: "10:30 AM",
    gateway: "Razorpay",
    gatewayReference: "pay_demo_1001",
  },
  {
    id: 2,
    transactionId: "TXN202610002",
    memberId: "SKM002",
    memberName: "Priya Devi",
    plan: "Basic Plan",
    amount: 999,
    method: "Card",
    status: "Pending",
    paymentDate: "2026-10-13",
    paymentTime: "11:15 AM",
    gateway: "Razorpay",
    gatewayReference: "pay_demo_1002",
  },
  {
    id: 3,
    transactionId: "TXN202610003",
    memberId: "SKM003",
    memberName: "Karthik Raj",
    plan: "Premium Plus",
    amount: 5999,
    method: "Net Banking",
    status: "Success",
    paymentDate: "2026-10-12",
    paymentTime: "02:45 PM",
    gateway: "PhonePe",
    gatewayReference: "pay_demo_1003",
  },
  {
    id: 4,
    transactionId: "TXN202610004",
    memberId: "SKM004",
    memberName: "Kavitha S",
    plan: "Silver Plan",
    amount: 1999,
    method: "UPI",
    status: "Failed",
    paymentDate: "2026-10-12",
    paymentTime: "04:20 PM",
    gateway: "Razorpay",
    gatewayReference: "pay_demo_1004",
  },
  {
    id: 5,
    transactionId: "TXN202610005",
    memberId: "SKM005",
    memberName: "Praveen Kumar",
    plan: "Gold Plan",
    amount: 3999,
    method: "Card",
    status: "Refunded",
    paymentDate: "2026-10-11",
    paymentTime: "09:10 AM",
    gateway: "PhonePe",
    gatewayReference: "pay_demo_1005",
  },
  {
    id: 6,
    transactionId: "TXN202610006",
    memberId: "SKM006",
    memberName: "Divya Lakshmi",
    plan: "Basic Plan",
    amount: 999,
    method: "UPI",
    status: "Success",
    paymentDate: "2026-10-10",
    paymentTime: "12:00 PM",
    gateway: "Razorpay",
    gatewayReference: "pay_demo_1006",
  },
  {
    id: 7,
    transactionId: "TXN202610007",
    memberId: "SKM007",
    memberName: "Suresh B",
    plan: "Premium Plus",
    amount: 5999,
    method: "Net Banking",
    status: "Pending",
    paymentDate: "2026-10-09",
    paymentTime: "03:35 PM",
    gateway: "PhonePe",
    gatewayReference: "pay_demo_1007",
  },
  {
    id: 8,
    transactionId: "TXN202610008",
    memberId: "SKM008",
    memberName: "Meena R",
    plan: "Silver Plan",
    amount: 1999,
    method: "UPI",
    status: "Success",
    paymentDate: "2026-10-08",
    paymentTime: "05:05 PM",
    gateway: "Razorpay",
    gatewayReference: "pay_demo_1008",
  },
];

function formatPrice(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

function formatDate(date) {
  if (!date) return "—";

  return new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const statusStyles = {
  Success: "bg-emerald-50 text-emerald-700",
  Pending: "bg-amber-50 text-amber-700",
  Failed: "bg-rose-50 text-rose-700",
  Refunded: "bg-violet-50 text-violet-700",
};

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
        statusStyles[status] || "bg-slate-100 text-slate-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          status === "Success"
            ? "bg-emerald-500"
            : status === "Pending"
            ? "bg-amber-500"
            : status === "Failed"
            ? "bg-rose-500"
            : "bg-violet-500"
        }`}
      />
      {status}
    </span>
  );
}

function StatCard({ title, value, description, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <h3 className="mt-2 text-xl font-bold text-slate-800 sm:text-2xl">
            {value}
          </h3>
          <p className="mt-2 text-[11px] text-slate-400">{description}</p>
        </div>

        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${colors[color]}`}
        >
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function PaymentDetailsModal({ payment, onClose }) {
  if (!payment) return null;

  const details = [
    ["Transaction ID", payment.transactionId],
    ["Member ID", payment.memberId],
    ["Member Name", payment.memberName],
    ["Membership Plan", payment.plan],
    ["Payment Method", payment.method],
    ["Payment Gateway", payment.gateway],
    ["Gateway Reference", payment.gatewayReference],
    ["Payment Date", formatDate(payment.paymentDate)],
    ["Payment Time", payment.paymentTime],
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              Payment Details
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Transaction information and payment status
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close payment details"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={19} />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-5 sm:p-6">
          <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-slate-500">Payment Amount</p>
                <h3 className="mt-1 text-2xl font-bold text-slate-800">
                  {formatPrice(payment.amount)}
                </h3>
              </div>
              <CreditCard size={27} className="text-blue-600" />
            </div>

            <div className="mt-3">
              <StatusBadge status={payment.status} />
            </div>
          </div>

          <div className="mt-5 divide-y divide-slate-100">
            {details.map(([label, value]) => (
              <div
                key={label}
                className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <span className="text-xs text-slate-500">{label}</span>
                <span className="break-all text-xs font-semibold text-slate-700 sm:max-w-[60%] sm:text-right">
                  {value || "—"}
                </span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Payments() {
  const [payments] = useState(initialPayments);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const stats = useMemo(() => {
    const successful = payments.filter((p) => p.status === "Success");
    const pending = payments.filter((p) => p.status === "Pending");
    const refunded = payments.filter((p) => p.status === "Refunded");

    return [
      {
        title: "Total Transactions",
        value: payments.length,
        description: "All payment records",
        icon: CreditCard,
        color: "blue",
      },
      {
        title: "Successful Payments",
        value: formatPrice(
          successful.reduce((sum, p) => sum + p.amount, 0)
        ),
        description: `${successful.length} successful transactions`,
        icon: CheckCircle2,
        color: "green",
      },
      {
        title: "Pending Payments",
        value: formatPrice(
          pending.reduce((sum, p) => sum + p.amount, 0)
        ),
        description: `${pending.length} awaiting confirmation`,
        icon: Clock3,
        color: "amber",
      },
      {
        title: "Refunded Amount",
        value: formatPrice(
          refunded.reduce((sum, p) => sum + p.amount, 0)
        ),
        description: `${refunded.length} refunded transactions`,
        icon: RotateCcw,
        color: "violet",
      },
    ];
  }, [payments]);

  const filteredPayments = useMemo(() => {
    const term = search.trim().toLowerCase();

    return payments.filter((payment) => {
      const matchesSearch =
        !term ||
        payment.transactionId.toLowerCase().includes(term) ||
        payment.memberId.toLowerCase().includes(term) ||
        payment.memberName.toLowerCase().includes(term);

      return (
        matchesSearch &&
        (!statusFilter || payment.status === statusFilter) &&
        (!methodFilter || payment.method === methodFilter) &&
        (!fromDate || payment.paymentDate >= fromDate) &&
        (!toDate || payment.paymentDate <= toDate)
      );
    });
  }, [
    payments,
    search,
    statusFilter,
    methodFilter,
    fromDate,
    toDate,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPayments.length / pageSize)
  );
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;

  const visiblePayments = filteredPayments.slice(
    startIndex,
    startIndex + pageSize
  );

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setMethodFilter("");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
          <span>Home</span>
          <span>/</span>
          <span>Payments</span>
          <span>/</span>
          <span className="font-medium text-blue-600">
            Payment History
          </span>
        </div>

        <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">
          Payment Management
        </h1>

        <p className="mt-1 text-xs text-slate-500 sm:text-sm">
          Track membership payments and review transaction details.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>

      {/* Filters */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-slate-800">
            Search & Filters
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Search using transaction ID, member name or member ID.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Transaction ID, name or member ID"
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
          >
            <option value="">All Payment Statuses</option>
            <option value="Success">Success</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
            <option value="Refunded">Refunded</option>
          </select>

          <select
            value={methodFilter}
            onChange={(e) => {
              setMethodFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
          >
            <option value="">All Payment Methods</option>
            <option value="UPI">UPI</option>
            <option value="Card">Card</option>
            <option value="Net Banking">Net Banking</option>
            <option value="Wallet">Wallet</option>
            <option value="Cash">Cash</option>
          </select>

          <div>
            <label className="mb-1.5 block text-[10px] font-medium text-slate-500">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[10px] font-medium text-slate-500">
              To Date
            </label>
            <input
              type="date"
              min={fromDate || undefined}
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">
            Found{" "}
            <strong className="text-slate-800">
              {filteredPayments.length}
            </strong>{" "}
            transaction(s)
          </p>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <RotateCcw size={14} />
            Reset Filters
          </button>
        </div>
      </section>

      {/* Payments table */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="text-sm font-bold text-slate-800">
              Payment Transactions
            </h2>
            <p className="mt-1 text-[11px] text-slate-400">
              Membership payment history
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            Show
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            entries
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left">
            <thead className="bg-slate-50">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Transaction</th>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Membership Plan</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payment Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {visiblePayments.map((payment) => (
                <tr
                  key={payment.id}
                  className="transition hover:bg-slate-50/70"
                >
                  <td className="whitespace-nowrap px-5 py-4">
                    <p className="text-xs font-semibold text-blue-600">
                      {payment.transactionId}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {payment.gateway}
                    </p>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <p className="text-xs font-semibold text-slate-800">
                      {payment.memberName}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {payment.memberId}
                    </p>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <span className="rounded-md bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">
                      {payment.plan}
                    </span>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4 text-xs font-bold text-slate-800">
                    {formatPrice(payment.amount)}
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      {payment.method === "UPI" ? (
                        <Smartphone size={15} className="text-slate-400" />
                      ) : payment.method === "Card" ? (
                        <CreditCard size={15} className="text-slate-400" />
                      ) : (
                        <Wallet size={15} className="text-slate-400" />
                      )}
                      {payment.method}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <StatusBadge status={payment.status} />
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <p className="text-xs text-slate-600">
                      {formatDate(payment.paymentDate)}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {payment.paymentTime}
                    </p>
                  </td>

                  <td className="whitespace-nowrap px-5 py-4">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        title="View payment details"
                        aria-label={`View ${payment.transactionId}`}
                        onClick={() => setSelectedPayment(payment)}
                        className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {visiblePayments.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center">
                    <ReceiptText
                      size={28}
                      className="mx-auto text-slate-300"
                    />
                    <p className="mt-3 text-sm font-semibold text-slate-700">
                      No payments found
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      Try changing your search or filters.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-[11px] text-slate-500">
            Showing {filteredPayments.length ? startIndex + 1 : 0} to{" "}
            {Math.min(startIndex + pageSize, filteredPayments.length)} of{" "}
            {filteredPayments.length} entries
          </p>

          <div className="flex items-center gap-1 self-end sm:self-auto">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (number) =>
                  number === 1 ||
                  number === totalPages ||
                  Math.abs(number - safePage) <= 1
              )
              .map((number, index, numbers) => (
                <React.Fragment key={number}>
                  {index > 0 && number - numbers[index - 1] > 1 && (
                    <span className="px-1 text-xs text-slate-400">
                      ...
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => setPage(number)}
                    className={`h-8 min-w-8 rounded-lg px-2 text-xs font-medium ${
                      safePage === number
                        ? "bg-blue-600 text-white"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {number}
                  </button>
                </React.Fragment>
              ))}

            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() =>
                setPage((p) => Math.min(totalPages, p + 1))
              }
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {selectedPayment && (
        <PaymentDetailsModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </div>
  );
}
